import { PlayCircleIcon } from 'lucide-react';
import { useState, type JSX } from 'react';

import type { CaseDetail } from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';

import { formatDuration } from '../quality-presentation.js';
import { chapterLabel } from './chapters.js';
import type { StepSeekHandler } from './recordings.js';
import { sharedStepName, topTimeSinks } from './time-sink.js';

/** Below this share of the run's step time the top steps are not a finding — every run has a
 *  slowest step, and saying so when it cost 4% of the time is noise above a table people came to
 *  read. */
const MIN_NOTEWORTHY_SHARE = 0.2;

/**
 * "Where the step time went" (S12, AC 28), as ONE collapsed line under the Cases header: the biggest
 * step-level contributors to a terminal run, expandable into per-step chips that seek the player
 * through the same `onSeek` path a timecode chip uses — never a second seek mechanism.
 *
 * The denominator is labelled "step time" rather than left bare. It is the sum of every step's
 * `duration_ms`, which is NOT the run's wall clock (the header and the Pipeline row both show a
 * larger number), and an unlabelled total next to two different totals on the same page reads as an
 * arithmetic error. Gated on `terminal` — it appears once the run finishes, not mid-run — and it
 * renders nothing when there is nothing worth saying: no cases, no step durations, or a top set too
 * small to matter.
 */
export function TimeSinkStrip({
  cases,
  terminal,
  onSeek,
}: {
  cases?: CaseDetail[];
  terminal: boolean;
  onSeek?: StepSeekHandler;
}): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  if (!terminal) return null;
  const { entries, entriesMs, totalMs } = topTimeSinks(cases);
  if (entries.length === 0 || totalMs <= 0) return null;
  if (entriesMs / totalMs < MIN_NOTEWORTHY_SHARE) return null;

  const shared = sharedStepName(entries);
  const summary = `Slowest steps: ${formatDuration(entriesMs)} of ${formatDuration(totalMs)} step time`;

  return (
    <div className="border-b border-border bg-muted/20 px-4 py-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{summary}</span>
        {shared ? (
          <span className="min-w-0 truncate">
            — all {entries.length} are &ldquo;{chapterLabel(shared)}&rdquo;
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => setExpanded((previous) => !previous)}
          aria-expanded={expanded}
          className="ml-auto shrink-0 rounded px-1 underline-offset-2 transition-colors hover:text-foreground hover:underline"
        >
          {expanded ? 'Hide' : 'Show'}
        </button>
      </div>
      {expanded ? (
        <ul className="mt-1.5 flex flex-wrap gap-1.5">
          {entries.map((entry, index) => {
            const canSeek = onSeek && typeof entry.timecodeMs === 'number';
            const stepName = chapterLabel(entry.stepName);
            const label = `#${entry.caseId} · ${stepName} · ${formatDuration(entry.durationMs)}`;
            return (
              <li
                key={`${entry.caseId}-${index}`}
                className="min-w-0 max-w-full text-xs"
              >
                {canSeek ? (
                  <button
                    type="button"
                    onClick={() =>
                      onSeek!(
                        entry.caseId,
                        entry.timecodeMs!,
                        entry.component,
                        undefined,
                        entry.videoKey,
                      )
                    }
                    aria-label={`Jump to ${stepName} in case ${entry.caseId} — ${formatDuration(entry.durationMs)}`}
                    className={cn(
                      'inline-flex max-w-full items-center gap-1 rounded-full border border-border px-2 py-0.5 text-muted-foreground transition-colors',
                      'hover:border-foreground/30 hover:text-foreground',
                    )}
                    title={`${entry.caseTitle} · ${entry.stepName}`}
                  >
                    <PlayCircleIcon className="size-3 shrink-0" aria-hidden />
                    <span className="truncate">{label}</span>
                  </button>
                ) : (
                  <span
                    className="inline-flex max-w-full items-center rounded-full border border-transparent px-2 py-0.5 text-muted-foreground"
                    title={`${entry.caseTitle} · ${entry.stepName}`}
                  >
                    <span className="truncate">{label}</span>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
